// User-pickable channel colors. Each Channel persists one of these on
// `channels.color` (see Channel::CHANNEL_COLORS on the Rails side — keep
// the two lists in sync). The same color name also drives `.theme-<color>`
// classes defined in `design-system.css`, which retint the design system's
// accent CSS vars for any descendants — that's how the piece detail page
// gets tabs/links/buttons in the piece's channel color.
//
// The Tailwind class strings below are the per-channel swatches used by
// calendar cards, filter chips, and channel badges. We pick from
// Tailwind's named palettes (Tailwind v4 ships them as CSS vars) so the
// shades read clearly in both light and dark mode.
export const CHANNEL_COLOR_NAMES = [
  "red", "orange", "amber", "emerald", "teal",
  "sky", "blue", "indigo", "violet", "pink"
] as const
export type ChannelColorName = typeof CHANNEL_COLOR_NAMES[number]

export type ChannelColor = {
  // Solid swatch — used for the dot in filter chips + the card's left rail.
  swatch: string
  // Soft background — used for badge / chip surfaces (subtle tint).
  bg: string
  // Border color used by the card outline.
  border: string
  // Strong border used when the card is dragging / focused.
  borderStrong: string
  // Foreground accent — used for headlines / chip text on saturated
  // surfaces (cardBg). High-contrast against the 200-level background.
  text: string
  // Icon tint for icons sitting on neutral (page / surface) backgrounds.
  // A mid-saturated shade so the channel color reads as itself instead
  // of as "very dark." Use this on channel-tinted tabs and
  // anywhere else an icon-only element renders without a colored
  // background behind it.
  icon: string
  // More saturated card surface — used on the calendar where each block
  // should read at a glance as belonging to a specific channel.
  cardBg: string
  cardBorder: string
  // Theme class to apply on a wrapper element so descendant
  // `bg-accent` / `text-accent` / `border-accent` / `*-accent-faded` /
  // `*-accent-display` utilities retint to this channel's hue.
  theme: string
}

const COLORS: Record<ChannelColorName, ChannelColor> = {
  red: {
    swatch:       "bg-red-500",
    bg:           "bg-red-50 dark:bg-red-950/30",
    border:       "border-red-200 dark:border-red-900",
    borderStrong: "border-red-500",
    text:         "text-red-900 dark:text-red-200",
    icon:         "text-red-500 dark:text-red-400",
    cardBg:       "bg-red-200 dark:bg-red-900/50",
    cardBorder:   "border-red-400 dark:border-red-700",
    theme:        "theme-red"
  },
  orange: {
    swatch:       "bg-orange-500",
    bg:           "bg-orange-50 dark:bg-orange-950/30",
    border:       "border-orange-200 dark:border-orange-900",
    borderStrong: "border-orange-500",
    text:         "text-orange-900 dark:text-orange-200",
    icon:         "text-orange-500 dark:text-orange-400",
    cardBg:       "bg-orange-200 dark:bg-orange-900/50",
    cardBorder:   "border-orange-400 dark:border-orange-700",
    theme:        "theme-orange"
  },
  amber: {
    swatch:       "bg-amber-500",
    bg:           "bg-amber-50 dark:bg-amber-950/30",
    border:       "border-amber-200 dark:border-amber-900",
    borderStrong: "border-amber-500",
    text:         "text-amber-900 dark:text-amber-200",
    icon:         "text-amber-500 dark:text-amber-400",
    cardBg:       "bg-amber-200 dark:bg-amber-900/50",
    cardBorder:   "border-amber-400 dark:border-amber-700",
    theme:        "theme-amber"
  },
  emerald: {
    swatch:       "bg-emerald-500",
    bg:           "bg-emerald-50 dark:bg-emerald-950/30",
    border:       "border-emerald-200 dark:border-emerald-900",
    borderStrong: "border-emerald-500",
    text:         "text-emerald-900 dark:text-emerald-200",
    icon:         "text-emerald-500 dark:text-emerald-400",
    cardBg:       "bg-emerald-200 dark:bg-emerald-900/50",
    cardBorder:   "border-emerald-400 dark:border-emerald-700",
    theme:        "theme-emerald"
  },
  teal: {
    swatch:       "bg-teal-500",
    bg:           "bg-teal-50 dark:bg-teal-950/30",
    border:       "border-teal-200 dark:border-teal-900",
    borderStrong: "border-teal-500",
    text:         "text-teal-900 dark:text-teal-200",
    icon:         "text-teal-500 dark:text-teal-400",
    cardBg:       "bg-teal-200 dark:bg-teal-900/50",
    cardBorder:   "border-teal-400 dark:border-teal-700",
    theme:        "theme-teal"
  },
  sky: {
    swatch:       "bg-sky-500",
    bg:           "bg-sky-50 dark:bg-sky-950/30",
    border:       "border-sky-200 dark:border-sky-900",
    borderStrong: "border-sky-500",
    text:         "text-sky-900 dark:text-sky-200",
    icon:         "text-sky-500 dark:text-sky-400",
    cardBg:       "bg-sky-200 dark:bg-sky-900/50",
    cardBorder:   "border-sky-400 dark:border-sky-700",
    theme:        "theme-sky"
  },
  blue: {
    swatch:       "bg-blue-600",
    bg:           "bg-blue-50 dark:bg-blue-950/30",
    border:       "border-blue-200 dark:border-blue-900",
    borderStrong: "border-blue-600",
    text:         "text-blue-900 dark:text-blue-200",
    icon:         "text-blue-500 dark:text-blue-400",
    cardBg:       "bg-blue-200 dark:bg-blue-900/50",
    cardBorder:   "border-blue-400 dark:border-blue-700",
    theme:        "theme-blue"
  },
  indigo: {
    swatch:       "bg-indigo-500",
    bg:           "bg-indigo-50 dark:bg-indigo-950/30",
    border:       "border-indigo-200 dark:border-indigo-900",
    borderStrong: "border-indigo-500",
    text:         "text-indigo-900 dark:text-indigo-200",
    icon:         "text-indigo-500 dark:text-indigo-400",
    cardBg:       "bg-indigo-200 dark:bg-indigo-900/50",
    cardBorder:   "border-indigo-400 dark:border-indigo-700",
    theme:        "theme-indigo"
  },
  violet: {
    swatch:       "bg-violet-500",
    bg:           "bg-violet-50 dark:bg-violet-950/30",
    border:       "border-violet-200 dark:border-violet-900",
    borderStrong: "border-violet-500",
    text:         "text-violet-900 dark:text-violet-200",
    icon:         "text-violet-500 dark:text-violet-400",
    cardBg:       "bg-violet-200 dark:bg-violet-900/50",
    cardBorder:   "border-violet-400 dark:border-violet-700",
    theme:        "theme-violet"
  },
  pink: {
    swatch:       "bg-pink-500",
    bg:           "bg-pink-50 dark:bg-pink-950/30",
    border:       "border-pink-200 dark:border-pink-900",
    borderStrong: "border-pink-500",
    text:         "text-pink-900 dark:text-pink-200",
    icon:         "text-pink-500 dark:text-pink-400",
    cardBg:       "bg-pink-200 dark:bg-pink-900/50",
    cardBorder:   "border-pink-400 dark:border-pink-700",
    theme:        "theme-pink"
  }
}

const FALLBACK: ChannelColor = {
  swatch:       "bg-zinc-400",
  bg:           "bg-zinc-50 dark:bg-zinc-900/50",
  border:       "border-hairline",
  borderStrong: "border-zinc-400",
  text:         "text-ink-display",
  icon:         "text-ink-body",
  cardBg:       "bg-zinc-100 dark:bg-zinc-800/50",
  cardBorder:   "border-zinc-300 dark:border-zinc-700",
  theme:        ""
}

export function channelColor(color: string | null | undefined): ChannelColor {
  if (!color) return FALLBACK
  return COLORS[color as ChannelColorName] || FALLBACK
}
